import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TILE_GUIDE } from '../../data/tileGuide';
import { ANIMAL_ICONS } from '../../theme';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { isAndroidApp, getPlayPrice, subscribe, restore, openPlayStore, PRODUCT_ID } from '../../utils/billing';

const GOLD = '#D4AF37';
const GOLD_HI = '#F5D97A';
const MOTTO = {
  quote: 'Words that crown your day.', ledger: 'Honour the souls who shaped you.', oath: 'Keep your word. Crave the win. Lock your focus.',
  power: 'Step into your empire.', energy: 'Fuel the machine that builds you.', peace: 'Breathe deep, stand unshakeable.',
  divine: 'Rise with sacred energy.', aegis: 'Guard your glow, inside and out.', destiny: 'Map the future you deserve.',
  vision: 'See it, then become it.', manifest: 'Speak it into existence.', pronoia: 'The universe conspires for you.',
  frequency: 'Tune into your highest self.', relief: 'Release, reset, return stronger.', tracker: 'Every streak builds a legend.',
  agenda: 'Command your day with intent.', book: 'Own every second.', signature: 'Find calm in perfect harmony.',
  echo: 'Sharpen the mind of a sovereign.',
};
const PILLARS = [
  ['fashion', 'Fashion', '#D4AF37'], ['food', 'Food', '#C8A95E'], ['explore', 'Explore', '#0F52BA'], ['souls', 'Soul', '#DC143C'],
  ['sporty', 'Sport', '#10B981'], ['success', 'Success', '#E5C44D'], ['legacy', 'Legacy', '#8B5CF6'], ['art', 'Art', '#2A9BD8'],
];
const ROTATE_MS = 3000;

const PillarStrip = () => (
  <div className="flex justify-between px-1" data-testid="upgrade-pillars">
    {PILLARS.map(([id, word, c], i) => {
      const ic = ANIMAL_ICONS[id];
      return (
        <div key={id} className="flex flex-col items-center gap-1" style={{ width: '12%' }}>
          <motion.span className="rounded-full overflow-hidden block" style={{ width: 30, height: 30, border: `1.5px solid ${c}` }}
            animate={{ boxShadow: [`0 0 4px ${c}55`, `0 0 14px ${c}`, `0 0 4px ${c}55`] }}
            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.3 }}>
            <img src={ic.src} alt="" className="w-full h-full object-cover" style={{ objectPosition: ic.pos }}/>
          </motion.span>
          <span className="font-['Outfit'] uppercase" style={{ fontSize: 7, letterSpacing: '0.12em', color: c }}>{word}</span>
        </div>
      );
    })}
  </div>
);

const Slide = ({ t, Icon }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5"
    style={{ background: `radial-gradient(ellipse at 50% 30%, ${t.accent}38, transparent 65%)` }}>
    <motion.div className="w-20 h-20 rounded-[22px] flex items-center justify-center mb-3"
      initial={{ scale: 0.6, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 14 }}
      style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.08), rgba(0,0,0,0.6))', border: `1.5px solid ${t.accent}`,
        boxShadow: `0 0 26px ${t.accent}88, inset 0 0 14px rgba(255,255,255,0.06)` }}>
      {Icon ? <Icon size={36} style={{ color: t.accent }}/> : null}
    </motion.div>
    <p className="font-['Cinzel'] uppercase" style={{ fontSize: 17, letterSpacing: '0.24em', color: t.accent, textShadow: `0 0 14px ${t.accent}99` }}>{t.name}</p>
    <p className="font-['Cormorant_Garamond'] italic text-white/70" style={{ fontSize: 14 }}>{t.tag}</p>
    <p className="font-['Outfit'] text-white/75 mt-2 leading-snug line-clamp-3" style={{ fontSize: 12 }}>{t.how}</p>
    <p className="font-['Cormorant_Garamond'] italic mt-2" style={{ fontSize: 16, color: GOLD_HI }}>“{MOTTO[t.id]}”</p>
  </div>
);

/* Bliss Premium (bliss_tile_222): rotating tile previews + Google Play checkout. */
export const UpgradeModal = ({ onClose, icons = {}, lockedTiles = [], productId = PRODUCT_ID, fallbackPrice = '₹349',
  customSlides, headline, testPrefix = 'upgrade', title = 'Bliss Premium' }) => {
  useHistoryClose(true, onClose);
  const slides = customSlides || TILE_GUIDE.filter((t) => lockedTiles.includes(t.id))
    .map((t) => ({ id: t.id, name: t.name, accent: t.accent, node: <Slide t={t} Icon={icons[t.id]}/> }));
  const [i, setI] = useState(0);
  const [price, setPrice] = useState(null);
  const [busy, setBusy] = useState('');
  const touch = useRef(null);
  const android = isAndroidApp();

  useEffect(() => { getPlayPrice(productId).then(setPrice); }, [productId]);
  useEffect(() => {
    if (!slides.length) return undefined;
    const tm = setTimeout(() => setI((n) => (n + 1) % slides.length), ROTATE_MS);
    return () => clearTimeout(tm);
  }, [i, slides.length]);

  const go = (d) => setI((n) => (n + d + slides.length) % slides.length);
  const run = async (kind) => {
    setBusy(kind);
    try {
      const res = kind === 'buy' ? await subscribe(productId) : await restore(productId);
      if (res?.pending) toast('Payment pending. Your tiles unlock once Google confirms it.');
      else if (res && (productId === PRODUCT_ID ? res.entitled : res.chat_entitled)) { toast('Welcome to Bliss Premium'); onClose(); }
      else if (kind === 'restore') toast('No active subscription found on this Google account');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Something went wrong';
      if (!/cancel/i.test(msg)) toast(msg);
    } finally { setBusy(''); }
  };
  const t = slides[i];

  return createPortal(
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 backdrop-blur-sm px-3" data-testid={`${testPrefix}-modal`}
      style={{ paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
        paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 12px)' }}>
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="relative w-full max-w-md max-h-full rounded-[28px] p-[1.5px] flex"
        style={{ background: `linear-gradient(135deg, ${GOLD_HI}, #6b5520 30%, ${GOLD} 55%, #3a2e10 80%, ${GOLD_HI})`,
        }}>
        <div className="w-full rounded-[27px] px-4 pt-4 pb-5 overflow-y-auto" style={{ background: 'linear-gradient(180deg, #0d0b08, #030303 60%)' }}>
          <button type="button" onClick={onClose} data-testid={`${testPrefix}-close`} aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10"
            style={{ border: `1px solid ${GOLD}66`, color: GOLD_HI }}><X size={15}/></button>
          <div className="flex items-center justify-center gap-2">
            <Crown size={15} color={GOLD_HI}/>
            <span className="font-['Cinzel'] uppercase" style={{ fontSize: 12, letterSpacing: '0.34em', color: GOLD_HI }}>{title}</span>
          </div>

          <div className="relative mt-3 rounded-2xl overflow-hidden" data-testid={`${testPrefix}-carousel`}
            style={{ height: 'clamp(200px, 32dvh, 250px)', border: `1px solid ${t ? t.accent : GOLD}66`, background: '#050505', transition: 'border-color 0.5s' }}
            onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
            <AnimatePresence mode="wait">
              {t && (
                <motion.div key={t.id} className="absolute inset-0" data-testid={`${testPrefix}-slide-${t.id}`}
                  initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45 }}>
                  {t.node}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
              {slides.map((s, n) => (
                <button key={s.id} type="button" onClick={() => setI(n)} aria-label={s.name} data-testid={`upgrade-dot-${s.id}`}
                  className="h-1 rounded-full" style={{ width: n === i ? 14 : 4, background: n === i ? s.accent : 'rgba(255,255,255,0.25)', transition: 'width 0.3s' }}/>
              ))}
            </div>
          </div>

          <div className="mt-3"><PillarStrip/></div>

          <p className="font-['Cormorant_Garamond'] text-center mt-3 leading-snug" style={{ fontSize: 16, color: '#EDE6D6' }} data-testid={`${testPrefix}-headline`}>
            {headline || <><span style={{ color: GOLD_HI }}>Step into your empire.</span> Upgrade your edge, curate your legacy, and claim elite mastery.</>}
          </p>

          <button type="button" onClick={() => (android ? run('buy') : openPlayStore())} disabled={!!busy}
            data-testid={`${testPrefix}-subscribe-btn`}
            className="mt-3 w-full h-12 rounded-full font-['Cinzel'] uppercase flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60"
            style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD} 55%, #9c7c1f)`, color: '#0a0806', fontSize: 13, letterSpacing: '0.18em', fontWeight: 700,
              boxShadow: `0 0 22px ${GOLD}66` }}>
            {busy === 'buy' ? <Loader2 size={16} className="animate-spin"/> : <Crown size={15}/>}
            Subscribe · {price || fallbackPrice} / month
          </button>
          {android && (
            <button type="button" onClick={() => run('restore')} disabled={!!busy} data-testid={`${testPrefix}-restore-btn`}
              className="mt-2 w-full text-center font-['Outfit'] uppercase" style={{ fontSize: 10, letterSpacing: '0.2em', color: `${GOLD_HI}AA` }}>
              {busy === 'restore' ? 'Restoring…' : 'Restore purchase'}
            </button>
          )}
          <p className="text-center font-['Outfit'] mt-2 text-white/35" style={{ fontSize: 9.5 }}>
            {android ? 'Billed monthly through Google Play. Cancel anytime in Play Store › Subscriptions.' : 'Subscriptions are purchased in the Bliss Android app on Google Play.'}
          </p>
        </div>
      </motion.div>
    </div>,
    document.getElementById('root') || document.body
  );
};

export { GOLD_HI };
export default UpgradeModal;
